/**
 * BotConfig — Secure API & Bot Parameter Management
 *
 * Stores all runtime configuration for the Telegram / OpenAI order bot
 * in Firestore `botConfig/main` so the BP server can pull them on startup.
 *
 * Access: owner ONLY — do NOT add other roles.
 * Firestore rule: allow read, write: if request.auth.token.role == "owner";
 */

import React, { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import './BotConfig.css';

const OWNER_ROLES = ['owner']; // SECURITY: strictly owner-only

// ─── Schema ──────────────────────────────────────────────────────────────────
// Each group has a key, label, icon, and list of param definitions.
// type: 'secret' | 'text' | 'number' | 'toggle' | 'select' | 'time'
const PARAM_GROUPS = [
  {
    key: 'telegram',
    label: 'Telegram Bot',
    icon: '✈️',
    description: 'Telegram bot credentials and authorized chat IDs.',
    params: [
      {
        key: 'TELEGRAM_BOT_TOKEN',
        label: 'Bot Token',
        type: 'secret',
        placeholder: '8284480096:AAGsWw…',
        hint: 'From @BotFather. Keep this secret.',
      },
      {
        key: 'TELEGRAM_ALLOWED_CHAT_ID',
        label: 'Allowed Chat ID',
        type: 'text',
        placeholder: '1467404480',
        hint: 'Chat ID allowed to interact with the bot.',
      },
      {
        key: 'TICKET_ALERT_CHAT_ID',
        label: 'Alert Chat ID',
        type: 'text',
        placeholder: '1467404480',
        hint: 'Chat ID that receives ticket volume alerts.',
      },
    ],
  },
  {
    key: 'openai',
    label: 'OpenAI / AI',
    icon: '🤖',
    description: 'OpenAI credentials and routing model settings.',
    params: [
      {
        key: 'OPENAI_API_KEY',
        label: 'API Key',
        type: 'secret',
        placeholder: 'sk-proj-…',
        hint: 'Your OpenAI project API key.',
      },
      {
        key: 'OPENAI_ROUTER_MODEL',
        label: 'Router Model',
        type: 'select',
        options: ['gpt-4o', 'gpt-4o-mini', 'gpt-5', 'gpt-5.2', 'gpt-4-turbo'],
        hint: 'Model used for order parsing & routing.',
      },
      {
        key: 'OPENAI_ROUTER_REASONING_EFFORT',
        label: 'Reasoning Effort',
        type: 'select',
        options: ['low', 'medium', 'high'],
        hint: 'Balances speed vs accuracy.',
      },
    ],
  },
  {
    key: 'alerts',
    label: 'Alert Thresholds',
    icon: '🔔',
    description: 'When and how aggressively to trigger volume alerts.',
    params: [
      {
        key: 'START_TICKET_VOLUME_ALERT',
        label: 'Enable Volume Alert',
        type: 'toggle',
        hint: 'Master switch for ticket-volume alerting.',
      },
      {
        key: 'TICKET_ALERT_MINUTES',
        label: 'Lookback Window (min)',
        type: 'number',
        placeholder: '20',
        hint: 'Count tickets in the last N minutes.',
      },
      {
        key: 'TICKET_ALERT_THRESHOLD',
        label: 'Alert Threshold (tickets)',
        type: 'number',
        placeholder: '8',
        hint: 'Fire an alert when ticket count exceeds this.',
      },
      {
        key: 'TICKET_ALERT_EVERY_MINUTES',
        label: 'Repeat Alert Every (min)',
        type: 'number',
        placeholder: '5',
        hint: 'Suppress duplicate alerts for this many minutes.',
      },
    ],
  },
  {
    key: 'schedule',
    label: 'Alert Schedule',
    icon: '⏰',
    description: 'Active hours during which alerts are sent.',
    params: [
      {
        key: 'TICKET_ALERT_ACTIVE_START',
        label: 'Active Start',
        type: 'time',
        placeholder: '7pm',
        hint: 'Alerts are only sent after this time (e.g. 7pm).',
      },
      {
        key: 'TICKET_ALERT_ACTIVE_END',
        label: 'Active End',
        type: 'time',
        placeholder: '11pm',
        hint: 'Alerts stop after this time (e.g. 11pm).',
      },
    ],
  },
  {
    key: 'notifications',
    label: 'Notification Channels',
    icon: '📲',
    description: 'Which channels receive alert messages.',
    params: [
      {
        key: 'TICKET_ALERT_NOTIFY_TELEGRAM',
        label: 'Notify via Telegram',
        type: 'toggle',
        hint: 'Send alerts to the alert chat ID via Telegram.',
      },
      {
        key: 'TICKET_ALERT_NOTIFY_IMESSAGE',
        label: 'Notify via iMessage',
        type: 'toggle',
        hint: 'Send alerts via macOS iMessage.',
      },
      {
        key: 'TICKET_ALERT_IMESSAGE_RECIPIENT',
        label: 'iMessage Recipients',
        type: 'text',
        placeholder: '9376540871,+13099665292',
        hint: 'Comma-separated phone numbers for iMessage.',
      },
    ],
  },
  {
    key: 'square',
    label: 'Square POS',
    icon: '💳',
    description: 'Square API credentials used by the Sales Dashboard. These are pulled server-side — never exposed to the browser.',
    params: [
      {
        key: 'SQUARE_TOKEN',
        label: 'Access Token',
        type: 'secret',
        placeholder: 'EAAAl…',
        hint: 'Square Production Access Token (Square Developer → Applications → Production → Access token).',
      },
      {
        key: 'SQUARE_LOCATION_ID',
        label: 'Location ID',
        type: 'text',
        placeholder: 'L0SXZNQGRR7FQ',
        hint: 'Square Location ID (Square Developer → Locations).',
      },
    ],
  },
];

// Build blank state from schema
function buildBlankValues() {
  const v = {};
  PARAM_GROUPS.forEach(g => g.params.forEach(p => {
    v[p.key] = p.type === 'toggle' ? false : '';
  }));
  return v;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function SecretField({ value, onChange, placeholder, disabled }) {
  const [show, setShow] = useState(false);
  return (
    <div className="bc__secret-wrap">
      <input
        type={show ? 'text' : 'password'}
        className="bc__input bc__input--secret"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder || ''}
        disabled={disabled}
        autoComplete="off"
        spellCheck={false}
      />
      <button
        type="button"
        className="bc__secret-eye"
        onClick={() => setShow(s => !s)}
        aria-label={show ? 'Hide' : 'Show'}
        tabIndex={-1}
      >
        {show ? (
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
            <line x1="1" y1="1" x2="23" y2="23"/>
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
        )}
      </button>
    </div>
  );
}

function ParamField({ param, value, onChange, saving }) {
  const disabled = saving;
  if (param.type === 'secret') {
    return <SecretField value={value} onChange={onChange} placeholder={param.placeholder} disabled={disabled} />;
  }
  if (param.type === 'toggle') {
    const checked = value === true || value === '1' || value === 1;
    return (
      <button
        type="button"
        className={`bc__toggle${checked ? ' bc__toggle--on' : ''}`}
        onClick={() => onChange(!checked)}
        disabled={disabled}
        role="switch"
        aria-checked={checked}
      >
        <span className="bc__toggle-knob" />
        <span className="bc__toggle-label">{checked ? 'Enabled' : 'Disabled'}</span>
      </button>
    );
  }
  if (param.type === 'select') {
    return (
      <select
        className="bc__input bc__input--select"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
      >
        <option value="">— select —</option>
        {param.options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (param.type === 'number') {
    return (
      <input
        type="number"
        className="bc__input"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={param.placeholder || ''}
        disabled={disabled}
        min="0"
      />
    );
  }
  // text / time
  return (
    <input
      type="text"
      className="bc__input"
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={param.placeholder || ''}
      disabled={disabled}
    />
  );
}

function ConfigGroup({ group, values, onChange, saving }) {
  const [collapsed, setCollapsed] = useState(false);
  return (
    <section className="bc__group">
      <button
        type="button"
        className="bc__group-header"
        onClick={() => setCollapsed(c => !c)}
      >
        <span className="bc__group-icon">{group.icon}</span>
        <span className="bc__group-title">{group.label}</span>
        <span className="bc__group-chevron">{collapsed ? '›' : '⌄'}</span>
      </button>
      {!collapsed && (
        <div className="bc__group-body">
          <p className="bc__group-desc">{group.description}</p>
          {group.params.map(param => (
            <div key={param.key} className="bc__field">
              <label className="bc__label">
                {param.label}
                <span className="bc__env-badge">{param.key}</span>
              </label>
              <ParamField
                param={param}
                value={values[param.key]}
                onChange={val => onChange(param.key, val)}
                saving={saving}
              />
              {param.hint && <p className="bc__hint">{param.hint}</p>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function BotConfig() {
  const { role } = useAuth();
  const [values, setValues] = useState(buildBlankValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(false);

  // Load from Firestore on mount
  useEffect(() => {
    if (!OWNER_ROLES.includes(role)) { setLoading(false); return; }
    getDoc(doc(db, 'botConfig', 'main'))
      .then(snap => {
        if (snap.exists()) {
          const data = snap.data();
          setValues(prev => ({ ...prev, ...data.params }));
          setSavedAt(data.updatedAt?.toDate ? data.updatedAt.toDate() : null);
        }
      })
      .catch(e => setError('Failed to load config: ' + e.message))
      .finally(() => setLoading(false));
  }, [role]);

  const handleChange = useCallback((key, val) => {
    setValues(prev => ({ ...prev, [key]: val }));
    setDirty(true);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await setDoc(doc(db, 'botConfig', 'main'), {
        params: values,
        updatedAt: serverTimestamp(),
      });
      const now = new Date();
      setSavedAt(now);
      setDirty(false);
    } catch (e) {
      setError('Save failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!OWNER_ROLES.includes(role)) {
    return (
      <div className="bc__page bc__access-denied">
        <div className="bc__denied-card">
          <span className="bc__denied-icon">🔒</span>
          <h2>Access Restricted</h2>
          <p>Bot configuration is only accessible to admins and owners.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bc__page">
      {/* Page header */}
      <header className="bc__page-header">
        <div className="bc__header-left">
          <h1 className="bc__page-title">
            <span className="bc__title-icon">⚙️</span>
            Bot Configuration
          </h1>
          <p className="bc__page-sub">
            Manage runtime parameters for the Telegram ordering bot.
            Changes are saved to Firebase and picked up on next server start.
          </p>
        </div>
        <div className="bc__header-right">
          {savedAt && !dirty && (
            <span className="bc__saved-badge">
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Saved {savedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          {dirty && <span className="bc__unsaved-badge">● Unsaved changes</span>}
          <button
            className={`bc__save-btn${saving ? ' bc__save-btn--loading' : ''}`}
            onClick={handleSave}
            disabled={saving || !dirty}
          >
            {saving ? (
              <><span className="bc__spinner" /> Saving…</>
            ) : (
              <><svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/>
                <polyline points="17 21 17 13 7 13 7 21"/>
                <polyline points="7 3 7 8 15 8"/>
              </svg> Save Config</>
            )}
          </button>
        </div>
      </header>

      {error && (
        <div className="bc__error-banner">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          {error}
        </div>
      )}

      {loading ? (
        <div className="bc__loading">
          <span className="bc__spinner bc__spinner--lg" />
          <p>Loading configuration…</p>
        </div>
      ) : (
        <div className="bc__groups">
          {PARAM_GROUPS.map(group => (
            <ConfigGroup
              key={group.key}
              group={group}
              values={values}
              onChange={handleChange}
              saving={saving}
            />
          ))}
        </div>
      )}

      {/* Shell export helper */}
      {!loading && (
        <section className="bc__export">
          <h3 className="bc__export-title">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
            </svg>
            Shell Export Preview
          </h3>
          <p className="bc__export-hint">Copy these exports to bootstrap your BP server locally.</p>
          <pre className="bc__export-pre">
            {PARAM_GROUPS.flatMap(g => g.params).map(p => {
              const v = values[p.key];
              const display = p.type === 'toggle'
                ? (v ? '1' : '0')
                : (v ?? '');
              return `export ${p.key}=${display}`;
            }).join('\n')}
          </pre>
          <button
            className="bc__copy-btn"
            onClick={() => {
              const text = PARAM_GROUPS.flatMap(g => g.params).map(p => {
                const v = values[p.key];
                const display = p.type === 'toggle' ? (v ? '1' : '0') : (v ?? '');
                return `export ${p.key}=${display}`;
              }).join('\n');
              navigator.clipboard.writeText(text);
            }}
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
              <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
            </svg>
            Copy to Clipboard
          </button>
        </section>
      )}
    </div>
  );
}
